// src/Pages/CurrencyDetail.jsx
import { colors as themeColors } from '../utils/theme';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Grid, Paper, Typography, TextField, Checkbox, IconButton, Button, CircularProgress, useTheme, useMediaQuery, Snackbar, Alert, Tooltip} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { colors } from '../utils/theme';

export default function CurrencyDetail({ loggedInUser }) {
    const navigate = useNavigate();
    const path = 'http://192.168.1.248:3103/currencymaster';
    const userId = loggedInUser?.userId;

    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [resetFlag, setResetFlag] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [limit] = useState(5);
    const [offset, setOffset] = useState(0);
    const [selectAll, setSelectAll] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [mode, setMode] = useState('search');
    const [filterOpen, setFilterOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        fetchData(0);
    }, [mode, resetFlag]);

    const fetchData = async (offsetValue) => {
        setLoading(true);
        try {
            let url = `${path}/search?mode=${mode}&offset=${offsetValue}&limit=${limit}`;
            if (mode === 'search' && searchTerm) {
                url += `&CurrencyName=${encodeURIComponent(searchTerm)}`;
            }
            const res = await axios.get(url);
            const { total, data: items = [] } = res.data;

            if (offsetValue === 0) {
                setTotalCount(total);
                setData(items);
            } else {
                setData(prev => [...prev, ...items]);
            }

            setOffset(offsetValue + items.length);
        } catch {
            setSnackbar({ open: true, message: 'Failed to fetch data.', severity: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleShowMore = () => fetchData(offset);

    const handleSearch = () => {
        setOffset(0);
        setMode('search');
        setResetFlag(f => f + 1);
    };

    const handleReset = () => {
        setSearchTerm('');
        setOffset(0);
        setMode('search');
        setResetFlag(f => f + 1);
        setSelectAll(false);
        setSelectedRows([]);
    };

    const handleSelectAll = () => {
        const toggled = !selectAll;
        setSelectAll(toggled);
        setSelectedRows(toggled ? data.map(d => d.currencyId) : []);
    };
    const handleRowToggle = id => { setSelectedRows(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id] ); };
    const handleDelete = async () => {
        try {
            await axios.post(`${path}/Delete`, { userId, objAryId: selectedRows });
            setSnackbar({ open: true, message: 'Deleted successfully.', severity: 'success' });
            handleReset();
        } catch {
            setSnackbar({ open: true, message: 'Delete failed.', severity: 'error' });
        }
    };

    return (
        <Box sx={{ p: 2, mt: 2 }}>
            {/* Top toolbar with Add, count, Export */}
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={6}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Tooltip title="Toggle filters">
                            <IconButton onClick={() => setFilterOpen(o => !o)} color="primary">
                                <FilterListIcon />
                            </IconButton>
                        </Tooltip>
                        <Button component={RouterLink} to="/main/currencyMaster" variant="contained" sx={{ fontWeight: 600 }}>
                            <AddIcon sx={{ mr: 0.5 }} /> Add New
                        </Button>
                    </Box>
                </Grid>

                {/* Show count */}
                <Grid item xs={12} sm={3}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {data.length}/{totalCount}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={3} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                    <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ fontWeight: 500 }}>
                        Export
                    </Button>
                </Grid>
            </Grid>
            {/* Filter panel */}
            {filterOpen && (
                <Paper elevation={1} sx={{ p: 2, mt: 2, borderRadius: 2, bgcolor: '#f4f8fb' }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}> <TextField fullWidth size="small" label="Currency Name" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} /> </Grid>
                        <Grid item xs={6} sm={3}> <Button fullWidth variant="contained" onClick={handleSearch}>Search</Button> </Grid>
                        <Grid item xs={6} sm={3}> <Button fullWidth variant="outlined" onClick={handleReset}>Reset</Button> </Grid>
                    </Grid>
                </Paper>
            )}

            {/* Bulk actions */}
            <Paper elevation={0} sx={{ my: 2, p: 1.5, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fafbfd', border: `1px solid ${themeColors.neutral.line}`, borderRadius: 2 }}>
                <Checkbox checked={selectAll} onChange={handleSelectAll} />
                <Tooltip title="Delete selected">
                    <span>
                        <IconButton onClick={handleDelete} color="error" disabled={!selectedRows.length}>
                            <DeleteIcon />
                        </IconButton>
                    </span>
                </Tooltip>
                <Box sx={{ flexGrow: 1 }} />
            </Paper>

            {/* Currency cards list */}
            {loading && offset === 0
                ? <Box py={3} textAlign="center"><CircularProgress /></Box>
                : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {data.length
                            ? data.map(item => (
                                <Paper key={item.currencyId} elevation={0} sx={{ display: 'flex', alignItems: 'center', p: 2, borderRadius: 2, bgcolor: '#f9fbfc', border: `1px solid ${themeColors.neutral.line}`, transition: '0.2s', '&:hover': { boxShadow: '0 2px 12px rgba(0,0,0,0.08)', bgcolor: themeColors.neutral.surface } }}>
                                    <Checkbox checked={selectedRows.includes(item.currencyId)} onChange={() => handleRowToggle(item.currencyId)} size="small" />
                                    <Box sx={{ width: 36, height: 36, bgcolor: colors.primary.main, color: 'white', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', mr: 2 }}>
                                        {item.CurrencyName.charAt(0).toUpperCase()}
                                    </Box>
                                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="h6" sx={{ cursor: 'pointer', fontWeight: 500 }} onClick={() => navigate(`/main/editCurrencyMaster/${item.currencyId}`)}>{item.CurrencyName} </Typography>
                                        <Typography variant="h4" color="text.secondary"> | </Typography>
                                        <Typography variant="body2" color="text.secondary"> Symbol: {item.CurrencySymbol || '—'} </Typography>
                                    </Box>
                                </Paper>
                            )) : <Typography align="center" color="text.secondary"> No records found. </Typography>
                        }
                    </Box>
                )
            }

            {/* Show More button & count */}
            {data.length < totalCount && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button variant="outlined" onClick={handleShowMore} disabled={loading}>
                        {loading ? 'Loading…' : 'Show More'}
                    </Button>
                </Box>
            )}

            {/* Feedback Snackbar */}
            <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar(s => ({ ...s, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert severity={snackbar.severity} variant="filled"> {snackbar.message} </Alert>
            </Snackbar>
        </Box>
    );
}